import React, { useState, useRef, useCallback, useEffect } from "react";
import { IonButton, IonPage, IonContent, IonSpinner } from "@ionic/react";
import Cropper from "react-easy-crop";
import { useIonRouter } from "@ionic/react";
import Header from "../../components/Header";
import useAuth from "../../hooks/useAuth";
import ImageChooser from "../../components/ui/ImageChooser";
import {
  compressImage,
} from "../../utils/imageCompression";
import {
  uploadProfilePicture,
} from "../../services/user";

interface ChangeProfilePicProps {
  onSave?: (imageDataUrl: string) => void;
  onCancel?: () => void;
}

const ChangeProfilePic: React.FC<ChangeProfilePicProps> = () => {
  const router = useIonRouter();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { updateUserProfile } = useAuth();

  // Only trigger the file dialog if no preview URL is set yet
  useEffect(() => {
    if (!previewUrl && fileInputRef.current) {
      const timer = setTimeout(() => {
        fileInputRef.current?.click();
      }, 500); // Small delay to ensure component is fully rendered
      return () => clearTimeout(timer);
    }
  }, [previewUrl]);

  const onCropComplete = useCallback(
    (croppedArea: any, croppedAreaPixels: any) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );
  const handleSaveImage = async () => {
    if (!croppedAreaPixels || !previewUrl) {
      console.error("Invalid crop or image reference");
      return;
    }

    setIsLoading(true);

    try {
      const image = new Image();
      image.src = previewUrl;

      image.onload = async () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          console.error("Failed to get canvas context");
          setIsLoading(false);
          return;
        }

        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;

        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          croppedAreaPixels.width,
          croppedAreaPixels.height
        );

        canvas.toBlob(
          async (blob) => {
            if (!blob) {
              setIsLoading(false);
              return;
            }

            try {
              const croppedFile = new File([blob], "avatar.jpg", {
                type: "image/jpeg",
                lastModified: Date.now(),
              });

              const compressedFile = await compressImage(croppedFile, {
                maxSizeMB: 0.5,
                maxWidthOrHeight: 1024,
                quality: 0.85,
              });

              const uploadedUrl = await uploadProfilePicture(compressedFile);

              updateUserProfile({
                ...user,
                avatar: uploadedUrl,
              } as any);

              router.push("/settings/edit-profile");
            } catch (error) {
              console.error("Error saving avatar:", error);
            } finally {
              setIsLoading(false);
            }
          },
          "image/jpeg",
          1.0
        );
      };

      image.onerror = () => setIsLoading(false);
    } catch (error) {
      console.error("Error processing image:", error);
      setIsLoading(false);
    }
  };
  return (
    <IonPage>
      {/* Use Header component as requested */}
      <Header
        pageTitle="Edit Profile Pic"
        showBackButton={true}
        onBackButtonClick={() => router.push("/settings/edit-profile")}
        showAvatar={false}
        showGreeting={false}
        showUserIcon={false}
      />

      <IonContent fullscreen className="ion-padding-horizontal-none">
        {/* Main content area */}
        <div style={{ backgroundColor: "#F5F5F5", height: "100%" }}>
          <ImageChooser
            onSaveImage={(imageDataUrl: string) => {
              setPreviewUrl(imageDataUrl);
            }}
          />
          {previewUrl && (
            <div
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Instruction text - Matches screenshot */}
              <div
                style={{
                  padding: "25px 20px",
                  textAlign: "center",
                }}
              >
                <p
                  style={{
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "normal",
                    margin: "0",
                    lineHeight: "1.4",
                    maxWidth: "300px",
                    marginLeft: "auto",
                    marginRight: "auto",
                  }}
                >
                  Zoom in/out and drag the image within the frame to center it
                  as needed.
                </p>
              </div>

              {/* The crop section - center it vertically and horizontally */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: "80px", // Add space for the button
                }}
              >
                {/* This is the circular frame - perfectly clear with no border or shadow */}
                <div
                  style={{
                    width: "300px",
                    height: "300px",
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: "50%",
                  }}
                >
                  {previewUrl && (
                    <Cropper
                      image={previewUrl}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                      cropShape="round"
                      showGrid={false}
                      objectFit="cover"
                      style={{
                        containerStyle: {
                          backgroundColor: "#f5f5f5",
                        },
                        cropAreaStyle: {
                          border: "none",
                          color: "rgba(255,255,255,1)",
                          boxShadow: "0 0 0 9999px rgba(245, 245, 245, 0.7)",
                        },
                        mediaStyle: {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Save button - Styled to match the screenshot */}
              <div
                className="flex items-center justify-center mb-4"
                style={{
                  position: "fixed",
                  bottom: "30px",
                  left: "0",
                  right: "0",
                  padding: "0 20px",
                }}
              >
                <IonButton
                  expand="block"
                  onClick={handleSaveImage}
                  disabled={isLoading}
                  className="primary-button"
                >
                  {isLoading ? <IonSpinner name="dots" /> : "Save"}
                </IonButton>
              </div>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ChangeProfilePic;
